import { z } from "zod";
import { getTablePermissions, getIgnoredColumnsByPermission, filterColumnsByPermission } from "@/lib/permissions";
import type { UserRoleType } from "@/types/auth";

// Columns that require read permission on related entities
const RELATIONSHIP_COLUMNS = {
  contact_clients: "clients",
} as const;

export const getContactTableConfig = (userRoles: UserRoleType[] | null, displayNames?) => {
  const permissions = getTablePermissions(userRoles, "contacts");

  // Get columns to hide based on relationship permissions
  const permissionIgnored = getIgnoredColumnsByPermission(userRoles, RELATIONSHIP_COLUMNS);

  const allFilterChoices = [
    {
      name: "id",
      kind: "text" as const,
      displayName: `${displayNames?.contacts?.name ?? "Contact"} Id`,
      menus: ["filter", "visible"] as const,
    },
    { name: "first_name", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "last_name", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "preferred_name", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "email_address", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "phone_number", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "contact_clients", kind: "text" as const, menus: ["filter", "visible"] as const },
    { name: "created_at", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
  ];

  return {
    entityName: "Contact",
    entityNamePlural: "Contacts",

    tableConfig: {
      ignore: ["tenant_id", "meta_data", "comments", ...permissionIgnored],
      readonly: ["id", "created_at"],
      // anchor: ["first_name"],
      validators: {
        email_address: (r: { cell: unknown }) => z.string().email().safeParse(r?.cell).success,
      },
      writeKeys: {
        preferred_name: "meta_data.preferred_name",
      },
      headerFunc: (name: string) => {
        switch (name) {
          case "id":
            return `${displayNames?.contacts?.name ?? "Contact"} Id`;
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "id") return value.toString().slice(0, 6).toUpperCase();
        if (key === "meta_data") return "";
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      dataTypes: {
        created_at: "date",
      },
    },

    filterChoices: filterColumnsByPermission(userRoles, allFilterChoices, RELATIONSHIP_COLUMNS),

    ...permissions,
    storageKey: "contact_flyout",
  };
};
